import { AfterViewInit, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HeaderService } from 'app/services/header/header.service';
import { LogicielCaisseAuthService } from 'app/services/logiciel-caisse/logiciel-caisse-auth.service';
import { LogicielCaisseModuleService } from 'app/services/logiciel-caisse/logiciel-caisse-module.service';
import { AccesLogicielCaisseService } from 'app/services/logiciel-caisse/acces-logiciel-caisse.service';
import { navigateLogicielCaisseReturnUrl, redirectHostedHubIfLocalhost } from 'app/services/logiciel-caisse/logiciel-caisse-return-url.util';
import { AuthSyncService } from 'app/services/auth/auth-sync.service';
import { ToastrService } from 'ngx-toastr';
import * as Feather from 'feather-icons';

@Component({
  selector: 'app-acces-logiciel-caisse-onboarding',
  templateUrl: './acces-logiciel-caisse-onboarding.component.html',
  styleUrls: ['./acces-logiciel-caisse-onboarding.component.scss'],
})
export class AccesLogicielCaisseOnboardingComponent implements OnInit, AfterViewInit {
  readonly steps = ['Activer le module', 'Créer un accès'];
  currentStep = 0;
  returnUrl = '/tiers/ticketCaisse/tous';
  /** Masque l’UI pendant le diagnostic — évite le flash « Créer un accès » sur hébergé. */
  checking = true;

  code_station = '';
  moduleActivated = false;
  acceptActivation = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly headerService: HeaderService,
    private readonly lcAuth: LogicielCaisseAuthService,
    private readonly moduleService: LogicielCaisseModuleService,
    private readonly accesService: AccesLogicielCaisseService,
    private readonly authSync: AuthSyncService,
    private readonly toastr: ToastrService
  ) {}

  ngOnInit(): void {
    // Setup LC toujours sur le hub hébergé (évite localhost:4190).
    if (redirectHostedHubIfLocalhost(this.authSync.buildAuthQueryParams())) {
      return;
    }

    const ctx = this.lcAuth.getErpContext();
    if (!ctx) {
      void this.router.navigate(['/pages/authentication/login-v2']);
      return;
    }

    this.code_station = ctx.code_station;
    this.returnUrl =
      this.route.snapshot.queryParamMap.get('returnUrl') || '/tiers/ticketCaisse/tous';
    this.moduleActivated = this.moduleService.isEnabled(ctx.code_station)
      || this.moduleService.isEnabled(this.lcAuth.getEffectiveCodeStation(ctx.erpUserId));
    this.acceptActivation = this.moduleActivated;

    if (this.moduleActivated) {
      this.currentStep = 1;
    }

    this.headerService.setConfig({
      title: 'Configuration logiciel caisse',
      buttons: [],
    });

    // Déjà connecté LC → retour immédiat sans afficher « Créer un accès ».
    if (this.lcAuth.isAuthenticated()) {
      this.checking = true;
      navigateLogicielCaisseReturnUrl(this.router, this.returnUrl);
      return;
    }

    this.lcAuth.ensureAuthenticatedSession().subscribe({
      next: (ok) => {
        if (ok && this.lcAuth.isAuthenticated()) {
          navigateLogicielCaisseReturnUrl(this.router, this.returnUrl);
          return;
        }
        this.loadSyncAndDecide(ctx);
      },
      error: () => this.loadSyncAndDecide(ctx),
    });
  }

  private loadSyncAndDecide(ctx: NonNullable<ReturnType<LogicielCaisseAuthService['getErpContext']>>): void {
    this.accesService.getSyncStatus(ctx.code_station, ctx.erpStationId).subscribe({
      next: (status) => {
        const effectiveCode = String(
          status.resolved_code_station || status.code_station || ctx.code_station
        ).trim();
        this.moduleService.enableCodes(ctx.code_station, effectiveCode);
        this.moduleActivated = true;
        this.acceptActivation = true;
        this.currentStep = 1;

        if (status.erp?.has_acces || status.sync_ok) {
          if (this.lcAuth.isAuthenticated()) {
            navigateLogicielCaisseReturnUrl(this.router, this.returnUrl);
            return;
          }
          this.checking = false;
          void this.router.navigate(['/tiers/acces-logiciel-caisse/add'], {
            queryParams: { returnUrl: this.returnUrl },
          });
          return;
        }
        this.currentStep = 1;
        this.moduleActivated = true;
        this.acceptActivation = true;
        this.checking = false;
      },
      error: () => {
        this.checking = false;
        this.toastr.warning(
          'Impossible de vérifier la synchronisation caisse (service ERP indisponible). Activez le module manuellement.',
          'Logiciel caisse'
        );
      },
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => Feather.replace(), 0);
  }

  retour(): void {
    void this.router.navigate(['/tiers/acces-logiciel-caisse'], {
      queryParams: { returnUrl: this.returnUrl },
    });
  }

  onTimelineStepSelected(index: number): void {
    if (index <= this.currentStep) {
      this.currentStep = index;
    }
  }

  activateModule(): void {
    if (!this.acceptActivation) {
      this.toastr.warning('Cochez la case pour activer le module.', 'Module caisse');
      return;
    }
    this.moduleService.enable(this.code_station);
    this.moduleActivated = true;
    this.toastr.success('Module logiciel de caisse activé.', 'Configuration');
    this.currentStep = 1;
    setTimeout(() => Feather.replace(), 0);
  }

  goToCreateAcces(): void {
    if (!this.moduleService.isEnabled(this.code_station)) {
      this.toastr.warning('Activez d’abord le module (étape 1).', 'Configuration');
      this.currentStep = 0;
      return;
    }
    void this.router.navigate(['/tiers/acces-logiciel-caisse/add'], {
      queryParams: { returnUrl: this.returnUrl },
    });
  }
}
